import { useEffect, useRef } from "react";

// Auto-lock the vault after inactivity (§20: 15 min) and when the tab is hidden.
//
// Hiding the tab used to lock INSTANTLY, which made a half-typed vault item disappear every time the
// user switched tabs to look a password up (vault.lock() unmounts AppShell via VaultGate). The hide
// lock is now deferred by `hideGraceMs` and cancelled if the tab comes back in time; a tab that stays
// hidden still locks. Pass 0 for the previous strict behaviour. The correctness fix for the lost
// input is vault/draft.ts (the draft survives the lock either way) — this is the comfort half.
export function useIdleLock(timeoutMs: number, onLock: () => void, hideGraceMs = 60_000) {
  const idleTimer = useRef<number | undefined>(undefined);
  const hideTimer = useRef<number | undefined>(undefined);
  useEffect(() => {
    const reset = () => {
      if (idleTimer.current) window.clearTimeout(idleTimer.current);
      idleTimer.current = window.setTimeout(onLock, timeoutMs);
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        if (hideGraceMs <= 0) {
          onLock();
          return;
        }
        if (hideTimer.current) window.clearTimeout(hideTimer.current);
        hideTimer.current = window.setTimeout(onLock, hideGraceMs);
      } else {
        if (hideTimer.current) window.clearTimeout(hideTimer.current);
        hideTimer.current = undefined;
        reset(); // coming back counts as activity
      }
    };
    const events = ["mousedown", "keydown", "scroll", "touchstart", "mousemove"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    document.addEventListener("visibilitychange", onVisibility);
    reset();
    return () => {
      if (idleTimer.current) window.clearTimeout(idleTimer.current);
      if (hideTimer.current) window.clearTimeout(hideTimer.current);
      events.forEach((e) => window.removeEventListener(e, reset));
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [timeoutMs, onLock, hideGraceMs]);
}
